import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import { QuantityValidationData } from 'components/consultations/UpsertConsultation';
import Input from 'components/final-form/Input';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import {
  EntriesConsumerRenderProps,
  ExtendedBaseEntryDto,
  withEntriesContext
} from 'components/segments/entires/EntriesContext';
import EntriesSegment from 'components/segments/entires/EntriesSegment';
import MedicamentModal from 'components/segments/medicaments/MedicamentModal';
import ProcedureModal from 'components/segments/procedures/ProcedureModal';
import ProceduresSegment, { IndexedSearchData } from 'components/segments/procedures/ProceduresSegment';
import SpecialProcedureModal from 'components/segments/procedures/SpecialProcedureModal';
import TarmedProcedureModal from 'components/segments/procedures/TarmedProcedureModal';
import moment from 'moment';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Dimmer, Grid, Loader, Modal } from 'semantic-ui-react';
import { editBlockProcedure, getUpsertBlockProcedureById, saveBlockProcedure } from 'services/blockProcedureServices';
import { EntryTable } from 'ts-types/api.enums';
import { BaseEntryDto, BlockProcedureDto, BlockProcedureEntryDto, UpsertBlockProcedureDto } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';
import { required } from 'utils/validatorUtils';

const leftSegmentStyle = {
  width: "45%",
  paddingRight: "1rem"
};

const rightSegmentStyle = {
  paddingTop: "3rem",
  width: "53%",
  paddingLeft: "1rem"
};

interface Props extends MessageSourceProps, AuthConsumerRenderProps, EntriesConsumerRenderProps {
  modalOpen: boolean,
  onCloseModal: () => {},
  onUpsertBlockProcedure: (blockProcedureDto: BlockProcedureDto) => {},
  setCacheSearchId: (searchId?: string) => void,
  blockProcedureId?: number,
  tariffId: number,
  tarmedTariff: boolean
}

interface State {
  blockProcedure?: UpsertBlockProcedureDto,
  errorMessages?: Array<string>,
  editEntry?: ExtendedBaseEntryDto,
  searchData: Partial<IndexedSearchData>,
  quantityValidationData: QuantityValidationData,
  editTarmedProcedureModalOpen: boolean,
  specialProcedureModalOpen: boolean
  editProcedureModalOpen: boolean,
  editMedicamentModalOpen: boolean,
  loadingBlockProcedureData: boolean,
  cancelTokenSource: CancelTokenSource
}

class UpsertBlockProcedure extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {tariffId, currentUser, blockProcedureId, setCurrentEntries} = this.props;

    const date = moment().format("YYYY-MM-DD");
    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      searchData: {
        validDate: date,
        tariffId: tariffId,
        mandateId: currentUser!.userConfig.mandateId
      },
      quantityValidationData: {
        validDate: date,
        electronicDataTransfer: false,
        tariffId: tariffId
      },
      editTarmedProcedureModalOpen: false,
      editProcedureModalOpen: false,
      editMedicamentModalOpen: false,
      specialProcedureModalOpen: false,
      loadingBlockProcedureData: !!blockProcedureId,
      cancelTokenSource: cancelTokenSource
    };

    const onFinally = () => {
      this.setState({
        loadingBlockProcedureData: false
      })
    };

    if (blockProcedureId) {
      getUpsertBlockProcedureById(blockProcedureId, cancelTokenSource)
          .then(response => {
            this.setState({
              blockProcedure: response
            });
            setCurrentEntries(response.blockProcedureEntries);
          })
          .catch(noop)
          .finally(onFinally)
    }
  }

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessages: errorMessage ? [errorMessage] : undefined
    })
  };

  setErrorMessages = (errorMessages?: Array<string>) => {
    this.setState({
      errorMessages: errorMessages
    })
  };

  toggleEditTarmedProcedureModal = () => {
    this.setState(({editTarmedProcedureModalOpen}: State) => ({
      editTarmedProcedureModalOpen: !editTarmedProcedureModalOpen
    }))
  };

  toggleSpecialProcedureModalOpen = () => {
    this.setState(({specialProcedureModalOpen}: State) => ({
      specialProcedureModalOpen: !specialProcedureModalOpen
    }))
  };

  toggleEditProcedureModal = () => {
    this.setState(({editProcedureModalOpen}: State) => ({
      editProcedureModalOpen: !editProcedureModalOpen
    }))
  };

  toggleEditMedicamentModal = () => {
    this.setState(({editMedicamentModalOpen}: State) => ({
      editMedicamentModalOpen: !editMedicamentModalOpen
    }))
  };

  onUpsertTarmedProcedure = (tarmedProcedure: ExtendedBaseEntryDto) => {

    this.setState({
      editEntry: tarmedProcedure
    });

    this.toggleEditTarmedProcedureModal();
  };

  onUpsertSpecialProcedure = (specialProcedure?: ExtendedBaseEntryDto) => {

    this.setState({
      editEntry: specialProcedure
    });

    this.toggleSpecialProcedureModalOpen();
  };

  onEditProcedure = (procedure: ExtendedBaseEntryDto) => {

    this.setState({
      editEntry: procedure
    });

    this.toggleEditProcedureModal();
  };

  onEditMedicament = (medicament: ExtendedBaseEntryDto) => {

    this.setState({
      editEntry: medicament
    });

    this.toggleEditMedicamentModal();
  };

  onCloseModal = () => {

    const {setCurrentEntries, onCloseModal} = this.props;

    setCurrentEntries([]);

    onCloseModal();
  };

  handleSubmit = async (values: Partial<UpsertBlockProcedureDto>) => {

    const {currentEntries, blockProcedureId, getMessage, setCurrentEntries, onUpsertBlockProcedure, tariffId} = this.props;
    const {cancelTokenSource} = this.state;

    let blockProcedureEntries: Array<BlockProcedureEntryDto> = [];

    const mapEntry = (entry: BaseEntryDto): BlockProcedureEntryDto => {
      return {
        ...entry,
        blockProcedureEntryId: entry.parentEntryId
      }
    };

    currentEntries.forEach(({quantityFromDb, ...currentEntry}: ExtendedBaseEntryDto) => {

      if (currentEntry.bulks) {
        const bulkEntries: Array<BlockProcedureEntryDto> | undefined = currentEntry.bulks
            .filter(bulk => bulk.quantity > 0 && bulk.code != currentEntry.code)
            .map(bulk => mapEntry(bulk));

        if (bulkEntries != undefined) {
          blockProcedureEntries.push(...bulkEntries);
        }
      }

      if (currentEntry.descendants) {
        const descendantEntries: Array<BlockProcedureEntryDto> | undefined = currentEntry.descendants
            .filter(descendant => descendant.quantity > 0)
            .map(descendant => mapEntry(descendant));

        if (descendantEntries != undefined) {
          blockProcedureEntries.push(...descendantEntries);
        }
      }

      blockProcedureEntries.push(mapEntry(currentEntry))
    });

    values.blockProcedureEntries = blockProcedureEntries;
    values.tariffId = tariffId;

    const handleError = () => this.setErrorMessage(getMessage("error.general"));

    if (blockProcedureId) {
      await editBlockProcedure(blockProcedureId, values, cancelTokenSource)
          .then(response => {
            onUpsertBlockProcedure(response);
            setCurrentEntries([]);
            this.onCloseModal();
          })
          .catch(handleError)
    } else {
      await saveBlockProcedure(values, cancelTokenSource)
          .then(response => {
            onUpsertBlockProcedure(response);
            setCurrentEntries([]);
            this.onCloseModal();
          })
          .catch(handleError)
    }
  };

  render(): React.ReactNode {

    const {loadingBlockProcedureData} = this.state;

    return loadingBlockProcedureData ? this.renderLoader() : this.renderModal();
  }

  renderModal(): React.ReactNode {

    const {blockProcedure} = this.state;
    const {modalOpen, getMessage} = this.props;

    return (
        <Modal
            open={modalOpen}
            onClose={this.onCloseModal}
            style={{width: "1500px"}}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("blockProcedures")}</Modal.Header>
          <FormModalContentContainer>
            <FinalForm
                onSubmit={this.handleSubmit}
                subscription={{pristine: true, submitting: true}}
                render={this.renderForm}
                initialValues={blockProcedure ? blockProcedure : {}}
            />
          </FormModalContentContainer>
        </Modal>
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {
      errorMessages,
      searchData,
      editTarmedProcedureModalOpen,
      editProcedureModalOpen,
      editMedicamentModalOpen,
      specialProcedureModalOpen,
      editEntry,
      quantityValidationData
    } = this.state;
    const {blockProcedureId, tarmedTariff, setCacheSearchId, getMessage} = this.props;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessages && errorMessages.length
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessages()}>
                    {
                      errorMessages.map((errorMessage, index) => {
                        return (
                            <div key={index}>{errorMessage}</div>
                        )
                      })
                    }
                  </StyledErrorMessage>
              )
              }
              <PraxFormGrid>
                <Grid.Column style={leftSegmentStyle}>
                  <ProceduresSegment
                      searchData={searchData}
                      parentId={blockProcedureId}
                      onUpsertTarmedProcedure={this.onUpsertTarmedProcedure}
                      onEditProcedure={this.onEditProcedure}
                      onEditMedicament={this.onEditMedicament}
                      onErrors={this.setErrorMessages}
                      onError={this.setErrorMessage}
                      setCacheSearchId={setCacheSearchId}
                      entryTable={EntryTable.BLOCK_PROCEDURE}
                  />
                </Grid.Column>
                <Grid.Column style={rightSegmentStyle}>
                  <Grid>
                    <Grid.Row>
                      <Grid.Column width={2} verticalAlign="middle">
                        {getMessage("description")}:
                      </Grid.Column>
                      <Grid.Column width={14} verticalAlign="middle">
                        <Field
                            name="description"
                            validate={required}
                            component={Input}
                            maxLength="30"
                            fluid
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                  <EntriesSegment
                      componentHeight="465px"
                      tarmedTariff={tarmedTariff}
                      headerText={getMessage("blockProcedures.value")}
                      onUpsertTarmedProcedure={this.onUpsertTarmedProcedure}
                      onUpsertSpecialProcedure={this.onUpsertSpecialProcedure}
                      onEditProcedure={this.onEditProcedure}
                      onEditMedicament={this.onEditMedicament}
                  />
                </Grid.Column>
                <TarmedProcedureModal
                    modalOpen={editTarmedProcedureModalOpen}
                    onCloseModal={this.toggleEditTarmedProcedureModal}
                    tarmedProcedure={editTarmedProcedureModalOpen ? editEntry : undefined}
                    tariffId={searchData.tariffId}
                    quantityValidationData={quantityValidationData}
                    entryTable={EntryTable.BLOCK_PROCEDURE}
                    validDate={searchData.validDate}
                />
                <ProcedureModal
                    modalOpen={editProcedureModalOpen}
                    onCloseModal={this.toggleEditProcedureModal}
                    procedure={editProcedureModalOpen ? editEntry : undefined}
                />
                {
                  editMedicamentModalOpen &&
                  <MedicamentModal
                      modalOpen={editMedicamentModalOpen}
                      onCloseModal={this.toggleEditMedicamentModal}
                      medicament={editMedicamentModalOpen ? editEntry : undefined}
                  />
                }
                <SpecialProcedureModal
                    modalOpen={specialProcedureModalOpen}
                    onCloseModal={this.toggleSpecialProcedureModalOpen}
                    specialProcedure={specialProcedureModalOpen ? editEntry : undefined}
                />
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={this.onCloseModal}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    )
  };

  renderLoader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <Dimmer active page>
          <Loader inline='centered' size="medium" inverted>{getMessage("consultationForm.loading")}</Loader>
        </Dimmer>
    )
  };
}

const UpsertBlockProcedureWrapper =
    withMessages(
        withAuthContext(
            withEntriesContext(
                UpsertBlockProcedure)));

export default UpsertBlockProcedureWrapper;
